function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const QUESTION_TYPE_RE = /^(单选题|多选题|填空题|问答题|选择题)/
export const HANDOUT_QUESTION_INDEX_STYLE = 'color:#111827;font-size:16px;margin-right:8px;font-weight:700;'

export function stripLeadingQuestionIndex(text) {
  return (text || '').replace(/^(\d+\.?\s*)+/, '').trim()
}

export function cleanupStrayQuestionIndexNodes(node) {
  if (!node || !node.childNodes) return
  const indexNode = node.querySelector('[data-handout-question-index], .handout-question-index')
  Array.from(node.childNodes).forEach(child => {
    if (child === indexNode) return
    if (child.nodeType === 3) {
      const compact = (child.textContent || '').replace(/\s+/g, '').trim()
      if (/^\d+\.?$/.test(compact)) {
        child.remove()
      }
      return
    }
    if (child.nodeType === 1 && child.tagName === 'STRONG') {
      const compact = (child.textContent || '').replace(/\s+/g, '').trim()
      if (/^\d+\.?$/.test(compact) && child !== indexNode) {
        child.remove()
      }
    }
  })
}

export function isQuestionIndexElement(node) {
  if (!node || node.nodeType !== 1) return false
  if (node.hasAttribute('data-handout-question-index') || (node.classList && node.classList.contains('handout-question-index'))) {
    return true
  }
  if (node.tagName === 'STRONG') {
    const compact = (node.textContent || '').replace(/\s+/g, '').trim()
    return /^\d+\.?$/.test(compact)
  }
  return false
}

export function buildQuestionIndexHtml(index) {
  if (!index) return ''
  return `<span class="handout-question-index" data-handout-question-index="true" style="${HANDOUT_QUESTION_INDEX_STYLE}">${index}.</span>`
}

export function isQuestionMetaText(text) {
  if (!text) return false
  return /(单选题|多选题|填空题|问答题|选择题)/.test(text) && (/[★☆]{2,}/.test(text) || /[/／]/.test(text))
}

/** 将题目元信息行拆成题型、知识点路径、试卷来源、星级 */
export function splitQuestionMetaLine(text) {
  const trimmed = (text || '').replace(/\s+/g, ' ').trim()
  const starMatch = trimmed.match(/[★☆]+/g)
  const stars = starMatch ? starMatch.join('') : ''
  const body = stripLeadingQuestionIndex(trimmed.replace(/[★☆]+/g, '').trim())
  if (!body) return { stars: '', typePart: '', knowledgePart: '', sourcePart: '' }

  let typePart = ''
  let knowledgePart = ''
  let sourcePart = ''

  const typeMatch = body.match(/^(单选题|多选题|填空题|问答题|选择题)(?:\s*[/／]\s*|\s+)?(.*)$/)
  if (typeMatch) {
    typePart = typeMatch[1]
    let rest = (typeMatch[2] || '').trim()
    const sourceMatch = rest.match(/^(.*?)(?:[/／]\s*)?来源[：:]\s*(.+)$/)
    if (sourceMatch) {
      knowledgePart = sourceMatch[1].replace(/[/／]\s*$/, '').trim()
      sourcePart = sourceMatch[2].trim()
    } else {
      knowledgePart = rest.replace(/[/／]\s*$/, '').trim()
    }
  } else {
    const sourceMatch = body.match(/^(.*?)(?:[/／]\s*)?来源[：:]\s*(.+)$/)
    if (sourceMatch) {
      typePart = sourceMatch[1].replace(/[/／]\s*$/, '').trim()
      sourcePart = sourceMatch[2].trim()
    } else {
      typePart = body
    }
  }

  return { stars, typePart, knowledgePart, sourcePart }
}

export function buildQuestionMetaInnerHtml(parts, escapeFn = escapeHtml) {
  const { typePart, sourcePart, stars } = parts
  const esc = escapeFn
  const cleanTypePart = stripLeadingQuestionIndex(typePart)
  const typeHtml = cleanTypePart
    ? `<b><span class="handout-question-type" data-handout-question-type="true">${esc(cleanTypePart)}</span></b>`
    : ''
  const sourceHtml = sourcePart
    ? `<b><span class="handout-question-source" data-handout-question-source="true" style="margin-left:8px;">来源：${esc(sourcePart)}</span></b>`
    : ''
  const difficultyHtml = stars
    ? `<span class="handout-question-difficulty" data-handout-question-difficulty="true" style="margin-left:8px;white-space:nowrap;">${esc(stars)}</span>`
    : ''
  return `${typeHtml}${sourceHtml}${difficultyHtml}`
}

export function stripHandoutQuestionKnowledgeSpans(root) {
  if (!root) return
  Array.from(root.querySelectorAll('[data-handout-question-knowledge], .handout-question-knowledge')).forEach(node => {
    node.remove()
  })
}

export function extractQuestionMetaPrefix(node) {
  if (!node || !node.childNodes) return ''
  const parts = []
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === 1 && isQuestionIndexElement(child)) {
      parts.push(child.outerHTML)
      continue
    }
    if (child.nodeType === 3) {
      const compact = (child.textContent || '').replace(/\s+/g, '').trim()
      if (!compact || /^\d+\.?$/.test(compact)) continue
    }
    if (child.nodeType === 3 && !(child.textContent || '').trim()) continue
    break
  }
  return parts.join('')
}

export function decorateHandoutQuestionMeta(root, options = {}) {
  if (!root) return
  stripHandoutQuestionKnowledgeSpans(root)
  const escapeFn = options.escapeHtml || escapeHtml
  Array.from(root.querySelectorAll('p,div')).forEach((node) => {
    if (node.closest('table')) return
    if (node.hasAttribute('data-handout-page-break') || node.classList.contains('handout-page-break-token')) return
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim()
    if (!isQuestionMetaText(text)) return
    const prefix = extractQuestionMetaPrefix(node)
    node.classList.add('handout-inline-question')
    node.removeAttribute('data-handout-meta-normalized')
    node.innerHTML = prefix + buildQuestionMetaInnerHtml(splitQuestionMetaLine(text), escapeFn)
  })
}

export function decorateHandoutPageBreaks(root) {
  if (!root) return
  Array.from(root.querySelectorAll(
    '[data-handout-page-break="true"],.handout-page-break-token,.handout-page-break'
  )).forEach((node) => {
    node.classList.add('handout-page-break-token')
    node.setAttribute('data-handout-page-break', 'true')
    node.removeAttribute('style')
    node.innerHTML = '<span class="handout-page-break-label">分页符</span>'
  })
  Array.from(root.querySelectorAll('p,div')).forEach((node) => {
    if (node.closest('table')) return
    if (node.hasAttribute('data-handout-page-break') || node.classList.contains('handout-page-break-token')) return
    const text = (node.textContent || '').replace(/\s+/g, '').trim()
    if (!text.includes('【分页符】') && text !== '分页符') return
    node.classList.add('handout-page-break-token')
    node.setAttribute('data-handout-page-break', 'true')
    node.innerHTML = '<span class="handout-page-break-label">分页符</span>'
  })
}

export function decorateHandoutHeadings(root) {
  if (!root) return
  Array.from(root.querySelectorAll('h1')).forEach((node) => {
    node.classList.add('handout-heading-1')
  })
}

export { QUESTION_TYPE_RE }
